<template>
  <div>
    <!-- Resumen Financiero -->
    <div class="columns is-multiline">
      <div class="column is-12">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h3>{{ i18n.financialSummary[iso] }}</h3>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div class="field is-grouped">
                <div class="control">
                  <b-button
                    v-if="canShowConceptSelector"
                    type="is-primary"
                    icon-left="credit-card"
                    icon-pack="fas"
                    @click="openPaymentSelector"
                  >
                    {{ i18n.selectPaymentConcepts[iso] || 'Seleccionar Conceptos de Pago' }}
                  </b-button>
                  <b-button
                    v-else
                    type="is-success"
                    icon-left="check"
                    icon-pack="fas"
                    disabled
                  >
                    {{ i18n.allConceptsPaid[iso] || 'Todos los Conceptos Pagados' }}
                  </b-button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-4">
        <div class="box has-background-info-light">
          <p class="heading">{{ i18n.totalContractValue[iso] }}</p>
          <p class="title">{{ formatCurrency(totalContractValue) }}</p>
        </div>
      </div>
      <div class="column is-4">
        <div class="box has-background-success-light">
          <p class="heading">{{ i18n.totalPaid[iso] }}</p>
          <p class="title">{{ formatCurrency(financingPaidAmount) }}</p>
        </div>
      </div>
      <div class="column is-4">
        <div class="box has-background-warning-light">
          <p class="heading">{{ i18n.remainingBalance[iso] }}</p>
          <p class="title">{{ formatCurrency(remainingBalance) }}</p>
        </div>
      </div>
    </div>

    <!-- Detalles del Financiamiento -->
    <hr />
    <div class="columns is-multiline">
      <div class="column is-12">
        <div class="content">
          <h3>{{ i18n.financingDetails[iso] }}</h3>
        </div>
      </div>
    </div>

    <!-- Información del Contrato Financiero -->
    <div class="columns is-multiline">
      <div class="column is-3">
        <b-field :label="i18n.contractPrice[iso]">
          <b-input
            :value="formatCurrency(detailsContract.PrecioContrato)"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="i18n.adminCost[iso]">
          <b-input
            :value="formatCurrency(detailsContract.CostoAdministracion)"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="i18n.downPayment[iso]">
          <b-input
            :value="formatCurrency(detailsContract.PagoInicial)"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="i18n.downPaymentPercentage[iso]">
          <b-input
            :value="detailsContract.PorcentajePagoInicial + '%'"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-multiline">
      <div class="column is-3">
        <b-field :label="i18n.balance[iso]">
          <b-input
            :value="formatCurrency(detailsContract.Saldo)"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="i18n.monthlyPayments[iso]">
          <b-input
            :value="detailsContract.Mensualidades"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="i18n.monthlyPayment[iso]">
          <b-input
            :value="formatCurrency(detailsContract.PagoMensual)"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="i18n.interestRate[iso]">
          <b-input
            :value="detailsContract.TasaInteres + '%'"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-multiline">
      <div class="column is-6">
        <b-field :label="i18n.firstPaymentDate[iso]">
          <b-input
            :value="formatDate(detailsContract.FechaPrimeraMensualidad)"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field :label="i18n.interestFactor[iso]">
          <b-input
            :value="detailsContract.FactorIA"
            readonly
            disabled
            type="text"
          ></b-input>
        </b-field>
      </div>
    </div>

    <!-- Pagos Adicionales -->
    <hr />
    <div class="columns is-multiline" v-if="additionalPayments && additionalPayments.length > 0">
      <div class="column is-12">
        <div class="content">
          <h3>{{ i18n.additionalPayments[iso] }}</h3>
        </div>
      </div>
      <div class="column is-12">
        <b-table
          :data="additionalPayments"
          :loading="loading"
        >
          <b-table-column field="FechaPagoDate" :label="i18n.paymentDate[iso]" centered>
            <template v-slot="props">
              {{ props.row.FechaPagoDate }}
            </template>
          </b-table-column>
          <b-table-column field="CargoPago" :label="i18n.amount[iso]" numeric>
            <template v-slot="props">
              ${{ props.row.CargoPago }}
            </template>
          </b-table-column>
          <b-table-column field="TipoPago" :label="i18n.paymentType[iso]" centered>
            <template v-slot="props">
              {{ props.row.TipoPago }}
            </template>
          </b-table-column>
          <b-table-column field="Pagado" :label="i18n.paymentStatus[iso]" centered>
            <template v-slot="props">
              <b-tag 
                :type="props.row.Pagado ? 'is-success' : 'is-warning'"
                size="is-medium"
              >
                {{ props.row.Pagado ? i18n.paid[iso] : i18n.pending[iso] }}
              </b-tag>
            </template>
          </b-table-column>
        </b-table>
      </div>
    </div>

    <!-- Pagos Condicionales (Closing Costs) -->
    <hr v-if="conditionalDownPayment && conditionalDownPayment.length > 0" />
    <div class="columns is-multiline" v-if="conditionalDownPayment && conditionalDownPayment.length > 0">
      <div class="column is-12">
        <div class="content">
          <h3>{{ i18n.closingCost[iso] }}</h3>
        </div>
      </div>
      <div class="column is-12">
        <b-table
          :data="conditionalDownPayment"
          :loading="loading"
        >
          <b-table-column field="FechaPagoDate" :label="i18n.paymentDate[iso]" centered>
            <template v-slot="props">
              {{ props.row.FechaPagoDate }}
            </template>
          </b-table-column>
          <b-table-column field="CargoPago" :label="i18n.amount[iso]" numeric>
            <template v-slot="props">
              ${{ props.row.CargoPago }}
            </template>
          </b-table-column>
          <b-table-column field="TipoPago" :label="i18n.paymentType[iso]" centered>
            <template v-slot="props">
              {{ props.row.TipoPago }}
            </template>
          </b-table-column>
          <b-table-column field="Pagado" :label="i18n.paymentStatus[iso]" centered>
            <template v-slot="props">
              <b-tag 
                :type="props.row.Pagado ? 'is-success' : 'is-warning'"
                size="is-medium"
              >
                {{ props.row.Pagado ? i18n.paid[iso] : i18n.pending[iso] }}
              </b-tag>
            </template>
          </b-table-column>
        </b-table>
      </div>
    </div>
    <hr />
    <div v-if="contractInfo && contractInfo.Id" class="columns is-multiline">
      <div class="column is-12">
        <div class="content">
          <h3>Plan de mensualidades</h3>
        </div>
        <FinancingScheduleTable :contract-id="contractInfo.Id" />
      </div>
    </div>
    <hr v-if="contractInfo && contractInfo.Id" />
    <div class="columns is-multiline">
      <div class="column is-12">
        <div class="content">
          <h3>{{ i18n.contractPaymentLinks[iso] }}</h3>
        </div>
      </div>
      <div class="column is-12">
        <div v-if="loadingFinancingPayments" class="has-text-centered">
          <b-loading :is-full-page="false" :active="true"></b-loading>
          <p>{{ i18n.loadingFinancingPayments[iso] }}</p>
        </div>
        
        <div v-else-if="financingPayments.length === 0" class="has-text-centered">
          <p class="has-text-grey">{{ i18n.noFinancingPayments[iso] }}</p>
        </div>
        
        <div v-else>
          <b-table
            :data="translatedFinancingPayments"
            :loading="loadingFinancingPayments"
            hoverable
            striped
          >
            <b-table-column field="conceptsText" :label="i18n.concepts[iso]">
              <template v-slot="props">
                <span 
                  class="has-text-info" 
                  :title="props.row.conceptsText"
                  style="display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                >
                  {{ props.row.conceptsText || `${props.row.conceptsCount} conceptos` }}
                </span>
              </template>
            </b-table-column>
            
            <b-table-column field="totalAmount" :label="i18n.amount[iso]" numeric>
              <template v-slot="props">
                <strong>{{ formatCurrency(props.row.totalAmount / 100) }}</strong>
              </template>
            </b-table-column>
            
            <b-table-column field="createdAt" :label="i18n.creationDate[iso]" centered>
              <template v-slot="props">
                {{ formatDate(props.row.createdAt) }}
              </template>
            </b-table-column>
            
            <b-table-column field="paymentDate" :label="i18n.paymentDate[iso]" centered>
              <template v-slot="props">
                {{ formatDate(props.row.paymentDate) }}
              </template>
            </b-table-column>
            
            <b-table-column field="status" :label="i18n.paymentStatus[iso]" centered>
              <template v-slot="props">
                <b-tag 
                  :type="getStatusTagType(props.row.status)"
                  size="is-medium"
                >
                  {{ getStatusText(props.row.status) }}
                </b-tag>
              </template>
            </b-table-column>

            <b-table-column field="actions" :label="i18n.actions[iso]" centered>
              <template v-slot="props">
                <b-button
                    v-if="props.row.status !== 'paid' && props.row.status !== 'expired'"
                    size="is-small"
                    type="is-info"
                    icon-left="email"
                    @click="resendPaymentLink(props.row)"
                    :loading="resendingPaymentId === props.row._id"
                >
                    {{ i18n.resendLink[iso] }}
                </b-button>
              </template>
            </b-table-column>
          </b-table>
          
          <!-- Paginación -->
          <div v-if="financingPaymentsPagination.pages > 1" class="has-text-centered mt-4">
            <b-pagination
              :total="financingPaymentsPagination.total"
              :per-page="financingPaymentsPagination.limit"
              :current.sync="currentPage"
              @change="loadFinancingPayments"
              order="centered"
              size="is-small"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Selección de Pagos (Checkout Sessions) -->
    <FinancingPaymentSelectorNew
      :isActive="showPaymentSelector"
      :contractInfo="contractInfo"
      :detailsContract="detailsContract"
      :totalPaidAmount="financingPaidAmount"
      :totalContractValue="totalContractValue"
      :remainingBalance="remainingBalance"
      :additionalPayments="availableAdditionalPayments"
      :conditionalDownPayment="availableConditionalDownPayments"
      :parseIntNumberDecimals="parseIntNumberDecimals"
      :i18n="i18n"
      :iso="iso"
      :allContractMembers="allContractMembers"
      :conceptProgress="conceptProgress"
      @close="closePaymentSelector"
      @checkout-session-created="handleCheckoutSessionCreated"
      @subscription-created="handleSubscriptionCreated"
    />


  </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import FinancingPaymentSelectorNew from './FinancingPaymentSelectorNew.vue';
import FinancingScheduleTable from '../FinancingScheduleTable.vue';

const API_URL = process.env.VUE_APP_API_URL;

export default {
  name: 'FinancingTab',
  components: {
    FinancingPaymentSelectorNew,
    FinancingScheduleTable
  },
  props: {
    detailsContract: {
      type: Object,
      required: true
    },
    additionalPayments: {
      type: Array,
      default: () => []
    },
    conditionalDownPayment: {
      type: Array,
      default: () => []
    },
    renovations: {
      type: Array,
      default: () => []
    },
    i18n: {
      type: Object,
      required: true
    },
    iso: {
      type: String,
      default: 'es'
    },
    loading: {
      type: Boolean,
      default: false
    },
    parseIntNumberDecimals: {
      type: Function,
      required: true
    },
    contractInfo: {
      type: Object,
      required: true
    },
    allContractMembers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showPaymentSelector: false,
      loadingComponent: null,
      financingPayments: [],
      loadingFinancingPayments: false,
      currentPage: 1,
      financingPaymentsPagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      resendingPaymentId: null,
      conceptProgress: {
        downPayment: { paid: 0, total: 0, percentage: 0 },
        adminCost: { paid: 0, total: 0, percentage: 0 },
        additionalPayment: { paid: 0, total: 0, percentage: 0 }
      }
    };
  },
  computed: {
    financingPaidAmount() {
      if (!this.financingPayments || this.financingPayments.length === 0) return 0
      
      return this.financingPayments
        .filter(payment => payment.status === 'succeeded' || payment.status === 'paid')
        .reduce((total, payment) => {
          return total + (payment.totalAmount / 100)
        }, 0)
    },
    totalContractValue() {
      if (!this.detailsContract) return 0
      
      const contractPrice = this.detailsContract.PrecioContrato || 0
      const adminCost = this.detailsContract.CostoAdministracion || 0
      
      let closingCosts = 0
      if (this.conditionalDownPayment && this.conditionalDownPayment.length > 0) {
        closingCosts = this.conditionalDownPayment.reduce((sum, payment) => {
          const amount = typeof payment.CargoPago === 'string' 
            ? this.parseIntNumberDecimals(payment.CargoPago) 
            : (parseFloat(payment.CargoPago) || 0)
          return sum + amount
        }, 0)
      }
      
      // Solo se suman los conditionalDownPayment (CDP), no los additionalPayments (ADP)
      return contractPrice + adminCost + closingCosts
    },
    remainingBalance() {
      return Math.max(0, this.totalContractValue - this.financingPaidAmount)
    },
    translatedFinancingPayments() {
      if (!this.financingPayments || this.financingPayments.length === 0) return []
      
      return this.financingPayments.map(payment => {
        const translatedPayment = { ...payment }
        
        if (!payment.concepts || payment.concepts.length === 0) {
          return translatedPayment
        }
        
        const translatedConceptNames = payment.concepts.map(concept => {
          const productKey = concept.productKey
          
          let translatedName = concept.productName
          
          const i18nKeyMappings = {
            'downPayment': 'downPayment',
            'adminCost': 'adminCost', 
            'additionalPayment': 'additionalPayment',
            'closingCost': 'closingCost'
          }
          
          const i18nKey = i18nKeyMappings[productKey] || productKey
          
          if (this.i18n && this.i18n[i18nKey] && this.i18n[i18nKey][this.iso]) {
            translatedName = this.i18n[i18nKey][this.iso]
          }
          
          return translatedName
        }).filter(name => name && name.trim())
        
        translatedPayment.conceptsText = translatedConceptNames.join(', ')
        
        return translatedPayment
      })
    },

    // Verificar si se puede mostrar el modal de conceptos
    canShowConceptSelector() {
      const concepts = ['downPayment', 'adminCost', 'additionalPayment']
      return concepts.some(conceptKey => {
        const concept = this.conceptProgress[conceptKey]
        return !concept || concept.percentage < 100
      })
    },

    // Verificar si todos los conceptos están pagados
    areAllConceptsPaid() {
      const concepts = ['downPayment', 'adminCost', 'additionalPayment']
      return concepts.every(conceptKey => {
        const concept = this.conceptProgress[conceptKey]
        return concept && concept.percentage >= 100
      })
    },

    // Calcular ADP disponibles restando pagos ya realizados
    availableAdditionalPayments() {
      if (!this.additionalPayments || this.additionalPayments.length === 0) {
        return []
      }

      return this.additionalPayments.map(adp => {
        const adpId = adp._id
        const originalAmount = typeof adp.CargoPago === 'string' 
          ? this.parseIntNumberDecimals(adp.CargoPago) 
          : (parseFloat(adp.CargoPago) || 0)

        // Buscar pagos realizados para este ADP específico
        let paidAmount = 0
        if (this.financingPayments && this.financingPayments.length > 0) {
          this.financingPayments.forEach(payment => {
            if ((payment.status === 'succeeded' || payment.status === 'paid') && 
                payment.concepts && payment.concepts.length > 0) {
              payment.concepts.forEach(concept => {
                if (concept.productKey === 'additionalPayment' && concept.adpId === adpId) {
                  paidAmount += (concept.amount / 100) // Convertir de centavos a dólares
                }
              })
            }
          })
        }

        const remainingAmount = Math.max(0, originalAmount - paidAmount)
        
        return {
          ...adp,
          CargoPago: remainingAmount,
          originalAmount: originalAmount,
          paidAmount: paidAmount,
          isFullyPaid: remainingAmount <= 0
        }
      }).filter(adp => adp.CargoPago > 0) // Solo mostrar ADP con monto pendiente
    },

    // Calcular ConditionalDownPayment disponibles restando pagos ya realizados
    availableConditionalDownPayments() {
      if (!this.conditionalDownPayment || this.conditionalDownPayment.length === 0) {
        return []
      }

      return this.conditionalDownPayment.map(cdp => {
        const cdpId = cdp._id
        const originalAmount = typeof cdp.CargoPago === 'string' 
          ? this.parseIntNumberDecimals(cdp.CargoPago) 
          : (parseFloat(cdp.CargoPago) || 0)

        // Buscar pagos realizados para este CDP específico
        let paidAmount = 0
        if (this.financingPayments && this.financingPayments.length > 0) {
          this.financingPayments.forEach(payment => {
            if ((payment.status === 'succeeded' || payment.status === 'paid') && 
                payment.concepts && payment.concepts.length > 0) {
              payment.concepts.forEach(concept => {
                if (concept.productKey === 'additionalPayment' && concept.cdpId === cdpId) {
                  paidAmount += (concept.amount / 100) // Convertir de centavos a dólares
                }
              })
            }
          })
        }

        const remainingAmount = Math.max(0, originalAmount - paidAmount)
        
        return {
          ...cdp,
          CargoPago: remainingAmount,
          originalAmount: originalAmount,
          paidAmount: paidAmount,
          isFullyPaid: remainingAmount <= 0
        }
      }).filter(cdp => cdp.CargoPago > 0) // Solo mostrar CDP con monto pendiente
    }
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      return moment.utc(timestamp).format('MM/DD/YYYY');
    },
    calculateTotalPaid() {
      let total = 0;
      
      if (this.detailsContract && this.detailsContract.PagoInicial) {
        total += parseFloat(this.detailsContract.PagoInicial) || 0;
      }
      
      if (this.additionalPayments && this.additionalPayments.length > 0) {
        total += this.additionalPayments.reduce((sum, payment) => {
          const amount = typeof payment.CargoPago === 'string' 
            ? this.parseIntNumberDecimals(payment.CargoPago) 
            : (parseFloat(payment.CargoPago) || 0);
          return sum + amount;
        }, 0);
      }
      
      if (this.conditionalDownPayment && this.conditionalDownPayment.length > 0) {
        total += this.conditionalDownPayment.reduce((sum, payment) => {
          const amount = typeof payment.CargoPago === 'string' 
            ? this.parseIntNumberDecimals(payment.CargoPago) 
            : (parseFloat(payment.CargoPago) || 0);
          return sum + amount;
        }, 0);
      }
      
      if (this.renovations && this.renovations.length > 0) {
        total += this.renovations.reduce((sum, renewal) => {
          return sum + (parseFloat(renewal.Cargo) || 0);
        }, 0);
      }
      
      return total;
    },
    calculateRemainingBalance() {
      if (!this.detailsContract) return 0;
      
      const contractPrice = this.detailsContract.PrecioContrato || 0;
      const totalPaid = this.calculateTotalPaid();
      
      return Math.max(0, contractPrice - totalPaid);
    },
    openPaymentSelector() {
      this.showPaymentSelector = true;
    },


    closePaymentSelector() {
      this.showPaymentSelector = false;
    },

    handleSubscriptionCreated(payload) {
      
      this.$buefy.toast.open({
        message: this.i18n.subscriptionCreatedSuccess[this.iso] || 'Suscripción creada exitosamente. Se ha enviado el enlace por correo.',
        type: 'is-success',
        duration: 5000
      });

      this.loadFinancingPayments();
    },
    handleCheckoutSessionCreated(payload) {
      
      this.$buefy.toast.open({
        message: this.i18n.paymentLinkCreated?.[this.iso] || 'Enlace de pago creado y enviado por correo electrónico.',
        type: 'is-success',
        duration: 5000
      })
      
      this.loadFinancingPayments()
      this.loadConceptProgress() // Recargar progreso después de crear pago
    },
    
    async loadFinancingPayments() {
      if (!this.contractInfo?.Id) {
        return
      }
      
      this.loadingFinancingPayments = true
      
      try {
        const response = await axios.get(`${API_URL}/financing/payments/${this.contractInfo.Id}`, {
          params: {
            page: this.currentPage,
            limit: this.financingPaymentsPagination.limit
          }
        })
        
        if (response.data.success) {
          this.financingPayments = response.data.data
          this.financingPaymentsPagination = response.data.pagination
        } else {
          console.error('Error en la respuesta del servidor:', response.data.error)
          this.$buefy.toast.open({
            message: this.i18n.errorLoadingFinancingPayments[this.iso],
            type: 'is-danger',
            duration: 5000
          })
        }
      } catch (error) {
        console.error('Error cargando financing payments:', error)
        this.$buefy.toast.open({
          message: this.i18n.errorLoadingFinancingPayments[this.iso],
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.loadingFinancingPayments = false
      }
    },

    async loadConceptProgress() {
      if (!this.contractInfo?.Id) {
        return
      }
      
      try {
        const response = await axios.get(`${API_URL}/financing/concept-progress/${this.contractInfo.Id}`)
        
        if (response.data.success) {
          this.conceptProgress = response.data.data
        }
      } catch (error) {
        console.error('Error cargando progreso de conceptos:', error)
      }
    },
    
    getStatusTagType(status) {
      const statusTypes = {
        'succeeded': 'is-success',
        'paid': 'is-success',
        'expired': 'is-danger',
        'cancelled': 'is-light',
        'pending': 'is-info'
      }
      return statusTypes[status] || 'is-light'
    },
    
    getStatusText(status) {
      const statusTexts = {
        'succeeded': this.i18n.succeeded[this.iso],
        'paid': this.i18n.paid[this.iso],
        'expired': this.i18n.expired[this.iso],
        'cancelled': this.i18n.cancelled[this.iso],
        'pending': this.i18n.pending[this.iso]
      }
      return statusTexts[status] || status
    },

    async resendPaymentLink(item) {
        const id = item._id
        this.resendingPaymentId = id
        try {
            const endpoint = item.type === 'bundle'
                ? `${API_URL}/financing/resend-bundle-link/${id}`
                : `${API_URL}/financing/resend-payment-link/${id}`
            const response = await axios.post(endpoint)
            this.$buefy.toast.open({
                message: response.data.message || this.i18n.resendLinkSuccess[this.iso],
                type: 'is-success',
                duration: 5000
            })
        } catch (error) {
            console.error('Error reenviando enlace de pago:', error)
            let errorMessage = this.i18n.resendLinkError[this.iso]
            if (error.response?.data?.error === 'Payment link has expired') {
                errorMessage = this.i18n.linkExpired[this.iso]
            }
            this.$buefy.toast.open({
                message: errorMessage,
                type: 'is-danger',
                duration: 5000
            })
        } finally {
            this.resendingPaymentId = null
        }
    }
  },
  mounted() {
    console.log('allContractMembers', this.allContractMembers)
    this.loadFinancingPayments()
    this.loadConceptProgress()
  }
};
</script>

<style scoped>
.box {
  border-radius: 8px;
}

.heading {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a4a4a;
  margin-bottom: 0.5rem;
}

.title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #363636;
  margin: 0;
}
</style>
